// src/pages/ItemMaster.jsx
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getAllInvoiceItems, addInvoiceItem, updateInvoiceItem, deleteInvoiceItem } from "../firebase/database";

export default function ItemMaster() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ name: "", rate: 0, unit: "piece" });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    try {
      const data = await getAllInvoiceItems();
      setItems(data);
    } catch (err) {
      console.error("Error loading items:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: name === "rate" ? parseFloat(value) || 0 : value }));
  };

  const resetForm = () => {
    setFormData({ name: "", rate: 0, unit: "piece" });
    setEditingItem(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || formData.rate <= 0) return;

    setSaving(true);
    try {
      if (editingItem) {
        await updateInvoiceItem(editingItem.id, formData);
      } else {
        await addInvoiceItem(formData);
      }
      await loadItems();
      resetForm();
    } catch (err) {
      console.error("Error saving item:", err);
      alert("Error saving item");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({ name: item.name, rate: item.rate || 0, unit: item.unit || "piece" });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!confirm("Are you sure you want to delete this item?")) return;
    try {
      await deleteInvoiceItem(id);
      await loadItems();
    } catch (err) {
      console.error("Error deleting item:", err);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(amount || 0);
  };

  const filteredItems = items.filter(item => 
    item.name.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "50vh" }}>
        <div style={{ color: "#6B7280", fontFamily: "DM Sans, sans-serif" }}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "24px" }}>
      <style>{`
        .item-header { display: flex; justify-content: space-between; align-items: center; marginBottom: 24px; gap: 16px; }
        .item-table-container { background: white; border-radius: 12px; boxShadow: 0 1px 3px rgba(0,0,0,0.1); overflow: hidden; }
        .item-mobile-cards { display: none; flex-direction: column; gap: 12px; }
        .item-card { background: white; border: 1px solid #E5E7EB; border-radius: 12px; padding: 16px; display: flex; flex-direction: column; gap: 12px; boxShadow: 0 1px 3px rgba(0,0,0,0.1); }
        .item-card-header { display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid #F3F4F6; padding-bottom: 10px; }
        .item-card-info { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; font-size: 13px; }
        .info-label { color: #6B7280; font-size: 11px; font-weight: 600; text-transform: uppercase; }
        .item-card-actions { display: flex; gap: 8px; flex-wrap: wrap; padding-top: 10px; border-top: 1px solid #F3F4F6; }

        @media (max-width: 768px) {
          .item-header { flex-direction: column; align-items: flex-start; }
          .item-header > button { width: 100%; }
          .item-table-container { display: none; }
          .item-mobile-cards { display: flex; }
          .search-input { width: 100% !important; max-width: none !important; }
        }
      `}</style>

      {/* Header */}
      <div className="item-header" style={{ marginBottom: 24 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
            <Link 
              to="/admin" 
              style={{ color: "#6B7280", textDecoration: "none", display: "flex", alignItems: "center" }}
            >
              <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <h1 style={{ fontSize: 28, fontWeight: 700, color: "#1F2937", margin: 0, fontFamily: "DM Sans, sans-serif" }}>
              Item Master
            </h1>
          </div>
          <p style={{ color: "#6B7280", margin: "4px 0 0 44px", fontFamily: "DM Sans, sans-serif" }}>
            Manage your product/service items
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          style={{
            padding: "12px 24px",
            background: "#3B82F6",
            color: "white",
            border: "none",
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 600,
            fontFamily: "DM Sans, sans-serif",
            cursor: "pointer",
          }}
        >
          + Add Item
        </button>
      </div>

      {/* Search */}
      <div style={{ marginBottom: 20 }}>
        <input
          type="text"
          className="search-input"
          placeholder="Search items..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ padding: "10px 16px", border: "1px solid #D1D5DB", borderRadius: 8, fontSize: 14, width: 300, fontFamily: "DM Sans", outline: "none" }}
        />
      </div>

      {/* Form Modal */}
      {showForm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16 }}>
          <div style={{ background: "white", borderRadius: 12, padding: 24, width: "100%", maxWidth: 450, maxHeight: "90vh", overflowY: "auto" }}>
            <h2 style={{ fontSize: 20, fontWeight: 600, color: "#1F2937", marginBottom: 20 }}>
              {editingItem ? "Edit Item" : "Add New Item"}
            </h2>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 14, fontWeight: 500, color: "#374151", marginBottom: 6 }}>Item Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  style={{ width: "100%", padding: "10px 12px", border: "1px solid #D1D5DB", borderRadius: 8, fontSize: 14, fontFamily: "DM Sans" }}
                  placeholder="e.g., Course Fee, Registration Fee"
                />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 480 ? "1fr" : "1fr 1fr", gap: 16, marginBottom: 20 }}>
                <div>
                  <label style={{ display: "block", fontSize: 14, fontWeight: 500, color: "#374151", marginBottom: 6 }}>Rate (₹) *</label>
                  <input
                    type="number"
                    name="rate"
                    min="0"
                    step="0.01"
                    value={formData.rate}
                    onChange={handleChange}
                    required
                    style={{ width: "100%", padding: "10px 12px", border: "1px solid #D1D5DB", borderRadius: 8, fontSize: 14, fontFamily: "DM Sans" }}
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 14, fontWeight: 500, color: "#374151", marginBottom: 6 }}>Unit</label>
                  <select
                    name="unit"
                    value={formData.unit}
                    onChange={handleChange}
                    style={{ width: "100%", padding: "10px 12px", border: "1px solid #D1D5DB", borderRadius: 8, fontSize: 14, fontFamily: "DM Sans", background: "white" }}
                  >
                    <option value="piece">Piece</option>
                    <option value="hour">Hour</option>
                    <option value="month">Month</option>
                    <option value="year">Year</option>
                    <option value="unit">Unit</option>
                  </select>
                </div>
              </div>
              <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
                <button
                  type="button"
                  onClick={resetForm}
                  style={{ padding: "10px 20px", background: "#E5E7EB", color: "#374151", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: "pointer" }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  style={{ padding: "10px 20px", background: saving ? "#9CA3AF" : "#3B82F6", color: "white", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: saving ? "not-allowed" : "pointer" }}
                >
                  {saving ? "Saving..." : editingItem ? "Update" : "Add Item"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="item-table-container">
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#F9FAFB", borderBottom: "1px solid #E5E7EB" }}>
              <th style={{ padding: "14px 20px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "#6B7280" }}>ITEM NAME</th>
              <th style={{ padding: "14px 20px", textAlign: "center", fontSize: 12, fontWeight: 600, color: "#6B7280" }}>UNIT</th>
              <th style={{ padding: "14px 20px", textAlign: "right", fontSize: 12, fontWeight: 600, color: "#6B7280" }}>RATE</th>
              <th style={{ padding: "14px 20px", textAlign: "center", fontSize: 12, fontWeight: 600, color: "#6B7280" }}>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ padding: 40, textAlign: "center", color: "#6B7280" }}>
                  No items yet. Click "Add Item" to create one.
                </td>
              </tr>
            ) : (
              filteredItems.map((item, index) => (
                <tr key={item.id} style={{ borderBottom: index < filteredItems.length - 1 ? "1px solid #E5E7EB" : "none" }}>
                  <td style={{ padding: "14px 20px", fontWeight: 600, color: "#1F2937" }}>{item.name}</td>
                  <td style={{ padding: "14px 20px", textAlign: "center", color: "#374151" }}>{item.unit}</td>
                  <td style={{ padding: "14px 20px", textAlign: "right", fontWeight: 600, color: "#1F2937" }}>{formatCurrency(item.rate)}</td>
                  <td style={{ padding: "14px 20px", textAlign: "center" }}>
                    <button
                      onClick={() => handleEdit(item)}
                      style={{ padding: "6px 12px", fontSize: 12, background: "#E5E7EB", color: "#374151", borderRadius: 6, border: "none", cursor: "pointer", marginRight: 8 }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      style={{ padding: "6px 12px", fontSize: 12, background: "#FEE2E2", color: "#DC2626", borderRadius: 6, border: "none", cursor: "pointer" }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="item-mobile-cards">
        {filteredItems.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#6B7280", background: "white", borderRadius: 12 }}>
            No items found.
          </div>
        ) : (
          filteredItems.map((item) => (
            <div key={item.id} className="item-card">
              <div className="item-card-header">
                <div style={{ fontSize: 16, fontWeight: 700, color: "#1F2937" }}>{item.name}</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: "#1F2937" }}>{formatCurrency(item.rate)}</div>
              </div>
              <div className="item-card-info">
                <div>
                  <div className="info-label">Unit</div>
                  <div style={{ fontWeight: 600, fontSize: 14, textTransform: "capitalize" }}>{item.unit}</div>
                </div>
              </div>
              <div className="item-card-actions">
                <button
                  onClick={() => handleEdit(item)}
                  style={{ flex: 1, padding: "8px 12px", fontSize: 13, background: "#E5E7EB", color: "#374151", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600 }}
                >
                  Edit Item
                </button>
                <button
                  onClick={() => handleDelete(item.id)}
                  style={{ flex: 1, padding: "8px 12px", fontSize: 13, background: "#FEE2E2", color: "#DC2626", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600 }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}